import type { AxiosInstance, AxiosResponse } from 'axios';
import type { ServiceResult } from '../types/common/ServiceResult';
import { apiClient } from '../client/client';

/**
 * These endpoints return bare 200 OK with no body per swagger.
 * Normalize into a ServiceResult so callers get a consistent shape.
 */
function toServiceResult(response: AxiosResponse): ServiceResult {
  const data = response.data;
  if (data && typeof data === 'object' && 'isSuccess' in data) return data;
  return { isSuccess: response.status >= 200 && response.status < 300, statusCode: response.status };
}

/**
 * Listing approval service – approve, reject, or suspend listings.
 * PUT /api/listing-approval/approve-or-reject/{statusId}/{listingId}
 * PUT /api/listing-approval/approve-or-reject/{statusId}  (bulk, body = uuid[])
 * PUT /api/listing-approval/suspend/{listingId}
 */
export class ListingApprovalService {
  private static readonly BASE = '/api/listing-approval';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async approveOrReject(statusId: number, listingId: string): Promise<ServiceResult> {
    const response = await this.client.put(
      `${ListingApprovalService.BASE}/approve-or-reject/${statusId}/${listingId}`
    );
    return toServiceResult(response);
  }

  async approveOrRejectBulk(statusId: number, listingIds: string[]): Promise<ServiceResult> {
    const response = await this.client.put(
      `${ListingApprovalService.BASE}/approve-or-reject/${statusId}`,
      listingIds
    );
    return toServiceResult(response);
  }

  async suspend(listingId: string): Promise<ServiceResult> {
    const response = await this.client.put(
      `${ListingApprovalService.BASE}/suspend/${listingId}`
    );
    return toServiceResult(response);
  }
}

export const listingApprovalService = new ListingApprovalService();
